import { HeaderColumn } from '@tableau/widgets-datagrid';
import { TabStyles } from '@tableau/widgets';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { CommonStyles } from '../../../common/react/CommonStyles';
import { CreateExplorerEvent } from '../../../common/telemetryEvents/TelemetryEventCreator';
import { ExplorerSortEvent, TelemetryEvents } from '../../../common/telemetryEvents/TelemetryEvents';
import { imgUrl } from '../../../common/utilities/ImgUrl';
import { ContentSortOptions } from '../../../ts/models/ContentSortOptions';
import { dataKeyToCrassFieldMap } from '../../../ts/models/sort/MixedContentInCrassSortOptions';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { Telemetry } from '../../../commonInfra/services/Telemetry';

// These are (mostly) a direct copy of common/DataGridCells
// Here they just use the classes from widgets-datagrid

// Borrowed from datagrid
// tslint:disable-next-line:no-any
export function measureWidth(componentOrElement: React.Component<any, any> | Element): number {
  const domNode: Element = ReactDOM.findDOMNode(componentOrElement) as Element;
  const rect = domNode.getBoundingClientRect();
  return Math.ceil(rect.right - rect.left);
}

export function getPrimaryOrderClause(currentSort: ReadonlyArray<IOrderClause>): Readonly<IOrderClause> {
  if (currentSort && currentSort.length > 0) {
    return currentSort[0];
  } else {
    return { field: undefined, ascending: false };
  }
}

export function doesOrderClauseSupportDirections(orderClause: Readonly<IOrderClause>): boolean {
  return ContentSortOptions.supportsAscAndDesc(orderClause.field);
}

export module SortableHeader {
  /**
   * Produces a header formatter that handles displaying sorted state.
   */
  export function Formatter(currentSort: ReadonlyArray<IOrderClause>, changeSort: (field: string) => void, tooltip?: JSX.Element): React.Factory<any> {
    const primaryOrderClause = getPrimaryOrderClause(currentSort);

    return React.createFactory(class extends React.Component<HeaderColumn.HeaderFormatterProps> {

      private hasReportedWidth: boolean = false;

      render() {
        const col = this.props.col;
        const isSortable = col.sortable !== false;
        const hCellStyles: React.CSSProperties = {
          ...CommonStyles.Layout.TextEllipsis,
          paddingRight: CommonStyles.Sizes.MediumSpace,
          textDecoration: isSortable && this.props.hovered ? 'underline' : 'none',
          color: this.isSorted ? TabStyles.Typography.Color.PrimaryDark : TabStyles.Typography.Color.PrimaryLight,
          boxSizing: 'border-box',
        };

        const supportsDirections = doesOrderClauseSupportDirections(primaryOrderClause);

        return (
          <div style={hCellStyles} onClick={this.handleClick} data-tb-test-id={`tb-list-view-header-${this.props.col.dataKey}`}>
            <ArrowIcon sorted={this.isSorted} ascending={this.ascending} supportsAscDesc={supportsDirections} />
            {col.headerText}
            {tooltip}
          </div>
        );
      }

      componentDidMount() {
        // getBoundingClientRect doesnt play nice with overflow: 'hidden' property.
        // so measure width of header and add a 6 pixel padding
        // to ensure we don't elide header column text on initial render
        if (!this.hasReportedWidth && this.props.reportWidthFn) {
          this.hasReportedWidth = true;
          this.props.reportWidthFn(this.props.col.name, measureWidth(this) + 6);
        }
      }

      private get isSorted(): boolean {
        return primaryOrderClause.field === this.props.col.dataKey
          || (dataKeyToCrassFieldMap[this.props.col.dataKey] && dataKeyToCrassFieldMap[this.props.col.dataKey] === primaryOrderClause.field);
      }

      private get ascending(): boolean {
        return primaryOrderClause.ascending;
      }

      private handleClick = () => {
        if (this.props.col.sortable) {
          changeSort(this.props.col.dataKey);
          this.sendTelemetryEvent();
        }
      }

      private sendTelemetryEvent() {
        // if the primaryOrderClause is for this column, then handleClick is reversing the order. If it
        // is not, it means the user first clicked the header and we are using the default direction value.
        const asc = (this.props.col.dataKey === primaryOrderClause.field)
          ? !primaryOrderClause.ascending
          : (this.props.col.direction === 'asc');

        const event = CreateExplorerEvent<ExplorerSortEvent>({
          type: TelemetryEvents.ExplorerSort,
          sortField: this.props.col.dataKey,
          ascending: asc,
          eventSource: 'column'
        });
        Telemetry.sendEvent(event);
      }
    });
  }

  export function ArrowIcon(props: { sorted: boolean, ascending: boolean, supportsAscDesc: boolean }) {
    // arrow takes up left padding of hcell
    const arrowWidth = CommonStyles.Sizes.MediumSpace;
    const arrowHeight = CommonStyles.Sizes.NormalIconSize;
    let backgroundUrl: string | undefined = undefined;

    if (props.sorted && props.supportsAscDesc) {
      backgroundUrl =
        props.ascending ? imgUrl('narrow_sort_arrow_ascending.svg') : imgUrl('narrow_sort_arrow_descending.svg');
    }
    const arrowStyles = {
      width: arrowWidth,
      height: arrowHeight,
      display: 'inline-block',
      verticalAlign: 'middle',
      backgroundPosition: 'center',
      backgroundSize: arrowWidth + 'px ' + arrowHeight + 'px',
      backgroundRepeat: 'no-repeat',
      backgroundImage: backgroundUrl
    };
    return <div style={arrowStyles} />;
  }

}
